import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, BookOpen, Share2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useInView } from '@/hooks/useInView';

type BlogPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  cover_image: string | null;
  created_at: string;
};

const BLOG_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/manage-blog`;
const SITE_URL = 'https://dryeshjitesh.com';

const getReadTime = (text: string) => Math.max(1, Math.ceil(text.length / 800));

export const BlogPreview = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const { ref, inView: isInView } = useInView(0.1);

  useEffect(() => {
    fetch(`${BLOG_URL}?action=public`)
      .then(r => r.json())
      .then(data => setPosts((Array.isArray(data) ? data : []).slice(0, 3)))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && posts.length === 0) return null;

  return (
    <section id="blog" className="py-20 sm:py-24 md:py-28" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-10 sm:mb-12 md:mb-14 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <BookOpen size={14} className="text-primary" />
            <span className="text-[10px] sm:text-xs font-bold text-primary uppercase tracking-[0.2em]">Blog</span>
          </div>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Latest Posts
          </h2>
          <div className="w-12 h-1 bg-accent rounded-full mx-auto mb-4" />
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto px-4">Thoughts on medicine, technology, and healthcare innovation.</p>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="backdrop-blur-sm bg-card/70 border border-border/50 rounded-2xl overflow-hidden animate-pulse">
                <div className="h-48 bg-muted" />
                <div className="p-6 space-y-3">
                  <div className="h-3 bg-muted rounded w-1/3" />
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {posts.map((post, i) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className={`group backdrop-blur-sm bg-card/80 border border-border/30 rounded-2xl overflow-hidden hover:shadow-glow hover:-translate-y-2 transition-all duration-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100 + 200}ms` }}
              >
                <div className="h-[3px] bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_100%] opacity-60 group-hover:opacity-100 group-hover:animate-[gradient-shift_3s_ease_infinite] transition-opacity" />
                <div className="h-48 overflow-hidden relative">
                  {post.cover_image ? (
                    <>
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                      <BookOpen size={28} className="text-primary/40" />
                    </div>
                  )}
                  {/* Number overlay */}
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/90 text-primary-foreground text-[11px] font-bold backdrop-blur-sm">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background/80 backdrop-blur-sm text-[10px] font-semibold text-muted-foreground">
                      <Clock size={10} /> {getReadTime(post.excerpt)} min read
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <Calendar size={12} />
                    {new Date(post.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  <h3 className="text-base font-bold text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                      Read more <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const url = `${SITE_URL}/blog/${post.slug}`;
                        if (navigator.share) {
                          navigator.share({ title: post.title, url });
                        } else {
                          navigator.clipboard.writeText(url);
                          toast.success('Link copied to clipboard');
                        }
                      }}
                      className="p-2 rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                      aria-label="Share post"
                    >
                      <Share2 size={14} />
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className={`text-center mt-10 transition-all duration-700 delay-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-semibold text-primary-foreground bg-primary rounded-xl hover:bg-primary/90 shadow-card hover:shadow-glow transition-all group"
          >
            View All Posts <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};
